/**
 * Lingue nelle tendine, con le voci in piu' (553, decisione di Davide del 3/11/2026).
 *
 * Una lingua ha un codice ISO, un nome principale e, a volte, altri nomi con cui compare nell'elenco in
 * ordine alfabetico: «Persiano (farsi)» accanto a «Farsi (persiano)», perche' il persiano si trovi sotto la P
 * e sotto la F. Le voci in piu' non sono codici: scegliendone una si salva il codice della lingua, e un dato
 * salvato si mostra sempre con il nome principale. Schede, mediazioni, lingue dei mediatori e statistiche
 * restano su un codice solo.
 *
 * Nella tendina ogni voce in piu' ha un valore suo («fas~Persiano (farsi)»), che codiceDaVoce riporta al
 * codice: il codice resta il valore del solo nome principale, e la tendina di un dato salvato mostra quello.
 *
 * Senza React e senza dipendenze dal browser: le regole sono provate in apps/api/test/lingue-voci.test.ts.
 */

/** Lingua come arriva da GET /riferimenti/lingue. */
export type LinguaCatalogo = { codice_iso: string; nome: string; altre_voci?: string[] | null }

/** Lingua per le tendine: il codice, il nome principale, le voci in piu'. */
export type Lingua = { codice: string; nome: string; altreVoci?: string[] }

const SEPARATORE = '~'
const collatore = new Intl.Collator('it', { sensitivity: 'base' })

/** Dalla risposta dell'API: il codice senza spazi (la colonna e' char(3)). */
export function lingueDalCatalogo(righe: LinguaCatalogo[]): Lingua[] {
  return righe.map((l) => ({ codice: l.codice_iso.trim(), nome: l.nome, altreVoci: l.altre_voci ?? [] }))
}

/**
 * Le voci di una tendina: ogni voce con il suo nome e, se ne ha, con le voci in piu', tutte in ordine
 * alfabetico. Senza voci in piu' l'ordine resta quello ricevuto (le sedi, per esempio, seguono la sede
 * madre). `escludi`: codici gia' scelti, che non si propongono di nuovo sotto nessun nome.
 */
export function opzioniConAltreVoci(
  voci: Lingua[], escludi: readonly string[] = [],
): { valore: string; etichetta: string }[] {
  const restano = voci.filter((v) => !escludi.includes(v.codice))
  const principali = restano.map((v) => ({ valore: v.codice, etichetta: v.nome }))
  const altre = restano.flatMap((v) => (v.altreVoci ?? [])
    .map((nome) => ({ valore: `${v.codice}${SEPARATORE}${nome}`, etichetta: nome })))
  if (!altre.length) return principali
  return [...principali, ...altre].sort((a, b) => collatore.compare(a.etichetta, b.etichetta)
    || Number(a.valore.includes(SEPARATORE)) - Number(b.valore.includes(SEPARATORE)))
}

/** Il codice da salvare per la voce scelta: quello della lingua anche per le voci in piu'. */
export function codiceDaVoce(valore: string): string {
  const fine = valore.indexOf(SEPARATORE)
  return fine < 0 ? valore : valore.slice(0, fine)
}

/** Il nome con cui si mostra un dato salvato: sempre quello principale. */
export function nomeLingua(lingue: Lingua[], codice: string): string {
  return lingue.find((l) => l.codice === codice.trim())?.nome ?? codice
}
